import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { partnerSelector } from "@dashboard/config/redux";
import { fetchGetOrders } from "@dashboard/modules/orders/fetchers/orders.fetchers";
import { Order } from "@dashboard/modules/orders/types/order.types";
import { ORDERS_STATE_GROUPS } from "@dashboard/modules/orders/types/order-filters.types";
import { canCreateOrderShipment } from "@dashboard/modules/orders/utils/order-shipment.utils";
import { useDeliveryToken } from "./use-delivery-token.hook";

export const useDeliveryOrderPicker = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useDeliveryToken();
  const partner = useSelector(partnerSelector);
  const userId = partner?.userId;

  const [showPicker, setShowPicker] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchGetOrders({
        token,
        page: 0,
        perPage: 100,
        orderBy: "desc",
        stageGroup: ORDERS_STATE_GROUPS.payedWithoutLogistic,
      });
      const eligible = (response.data || []).filter(canCreateOrderShipment);
      setOrders(eligible);
    } catch {
      setError(t("delivery.home.orderPicker.error"));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  const openOrderPicker = useCallback(() => {
    setShowPicker(true);
    loadOrders();
  }, [loadOrders]);

  const closeOrderPicker = useCallback(() => {
    setShowPicker(false);
  }, []);

  const selectOrder = useCallback(
    (order: Order) => {
      if (!userId) return;
      setShowPicker(false);
      navigate(`/${userId}/delivery/create/${order.id}`);
    },
    [navigate, userId]
  );

  return {
    showPicker,
    orders,
    loading,
    error,
    openOrderPicker,
    closeOrderPicker,
    selectOrder,
    retryLoadOrders: loadOrders,
  };
};
